import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  SEASONS, TYPE_OF_MEMORY, REFLECTIONS, REFLECTION_TYPES, FREE_SEASON, PRICING,
} from "@/data/categories";
import { api } from "@/lib/api";
import { BrandLockup } from "@/components/Branding";
import { toast } from "sonner";
import {
  Plus, LockSimple, LockOpen, X, Check, CaretLeft, DownloadSimple, Lightbulb, CalendarBlank,
} from "@phosphor-icons/react";

const inputCls =
  "bg-white border-[#4A4540]/20 focus-visible:ring-[#E07A5A] text-[#2C2A28]";

const emptyForm = {
  season: FREE_SEASON,
  memory_type: "",
  title: "",
  event_date: "",
  location: "",
  reflection_type: "",
  reflection_item: "",
  description: "",
  feeling: "",
  photo_reminder: "",
};

export default function AddMemory() {
  const navigate = useNavigate();

  const seasonParam = new URLSearchParams(window.location.search).get("season");

  const [trial, setTrial] = useState({ is_pro: false, days_left: null });
  const [form, setForm] = useState({ ...emptyForm, season: seasonParam || FREE_SEASON });
  const [saving, setSaving] = useState(false);

  const isPro = trial.is_pro;

  const load = useCallback(async () => {
    try {
      const t = await api.get("/trial");
      if (t.data.paused) { toast.error("Your diary is paused. Upgrade to add new memories."); navigate("/timeline"); return; }
      setTrial(t.data);
    } catch (e) { /* non-blocking */ }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSave = form.title.trim().length > 0 && !!form.memory_type && !!form.season;

  const submit = async () => {
    if (!canSave) { toast.error("Add a title and choose a type of memory."); return; }
    setSaving(true);
    try {
      const isGrowth = form.reflection_type === "Personal Growth";
      await api.post("/events", {
        entry_type: "memory",
        season: form.season,
        title: form.title,
        memory_type: form.memory_type,
        custom_event: form.memory_type,
        event_date: form.event_date,
        location: form.location,
        description: form.description,
        reflection_type: form.reflection_type || null,
        reflection_item: form.reflection_item || null,
        reflections_strengths: isGrowth && form.reflection_item ? [form.reflection_item] : [],
        reflections_lessons: !isGrowth && form.reflection_item ? [form.reflection_item] : [],
        feelings: form.feeling ? [form.feeling] : [],
        photo_reminder: form.photo_reminder.trim() || null,
      });
      toast.success("Memory saved to your journey");
      setForm({ ...emptyForm, season: isPro ? form.season : FREE_SEASON });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "locked_feature") toast.error("That season is available with Lifetime Access.");
      else toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const reflectionGroups = form.reflection_type ? REFLECTIONS[form.reflection_type] : [];

  return (
    <div className="min-h-screen bg-[#FBF4E8] text-[#2C2A28]">
      {/* Header */}
      <header className="border-b border-[#4A4540]/10 bg-[#FBF4E8]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            <BrandLockup />
          </div>
          <button
            data-testid="back-to-journey"
            onClick={() => navigate("/timeline")}
            className="flex items-center gap-2 rounded-full border border-[#E07A5A]/40 bg-[#F6E2D8] px-4 py-2 text-sm font-semibold text-[#A84A32] shadow-sm transition-colors hover:bg-[#F0D2C4]"
          >
            <X size={16} weight="bold" /> <span className="hidden sm:inline">Exit to my journey</span><span className="sm:hidden">Exit</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        {/* Title */}
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#A84A32]">Your private story space</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight text-[#2C2A28] sm:text-5xl">Add a life memory</h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#6B635B]">
          There is no perfect way to tell your story. Begin with what you remember, then return whenever you are ready.
        </p>

        {/* Two-column layout */}
        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          {/* Form */}
          <section className="min-w-0 lg:col-span-2" data-testid="add-memory-form">
            <div className="overflow-hidden rounded-2xl border border-[#4A4540]/10 bg-white shadow-sm">
              <div className="h-1.5 w-full bg-gradient-to-r from-[#E05A5A] via-[#E8945A] to-[#E0A94C]" />
              <div className="space-y-5 p-6 sm:p-8">
                <div className="grid gap-5 sm:grid-cols-2 [&>*]:min-w-0">
                  <div>
                    <Label className="text-[#4A4540]">Seasons of Life</Label>
                    <Select value={form.season} onValueChange={(v) => set("season", v)}>
                      <SelectTrigger data-testid="season-select" className={inputCls}>
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 bg-white">
                        {SEASONS.map((s) => {
                          const locked = !isPro && s.id !== FREE_SEASON;
                          return (
                            <SelectItem key={s.id} value={s.id} disabled={locked} data-testid={`season-option-${s.id}`}>
                              {s.label}{locked ? " · Locked" : s.free ? " · Free" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#4A4540]">Type of Memory</Label>
                    <Select value={form.memory_type} onValueChange={(v) => set("memory_type", v)}>
                      <SelectTrigger data-testid="memory-type-select" className={inputCls}>
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {TYPE_OF_MEMORY.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`memory-type-${t.id.replace(/\s+/g, "-").toLowerCase()}`}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[#4A4540]">Give this memory a title</Label>
                  <Input data-testid="memory-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="A short title for this memory" className={inputCls} />
                </div>

                <div className="grid gap-5 sm:grid-cols-2 [&>*]:min-w-0">
                  <div>
                    <Label className="text-[#4A4540]">Date</Label>
                    <div className="relative">
                      <Input data-testid="memory-date-input" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} />
                      <CalendarBlank size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7F76]" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4A4540]">Location</Label>
                    <Input data-testid="memory-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Where did this happen?" className={inputCls} />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2 [&>*]:min-w-0">
                  <div>
                    <Label className="text-[#4A4540]">Reflections</Label>
                    <Select
                      value={form.reflection_type}
                      onValueChange={(v) => setForm((f) => ({ ...f, reflection_type: v, reflection_item: "" }))}
                    >
                      <SelectTrigger data-testid="reflection-type-select" className={inputCls}>
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {REFLECTION_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`reflection-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#4A4540]">Choose from the list</Label>
                    <Select
                      value={form.reflection_item}
                      onValueChange={(v) => set("reflection_item", v)}
                      disabled={!form.reflection_type}
                    >
                      <SelectTrigger data-testid="reflection-item-select" className={inputCls}>
                        <SelectValue placeholder={form.reflection_type ? "Choose one" : "Pick a reflection type first"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 bg-white">
                        {reflectionGroups.map((g) => (
                          <SelectGroup key={g.group}>
                            <SelectLabel className="text-[#A84A32]">{g.group}</SelectLabel>
                            {g.items.map((it) => (
                              <SelectItem key={it} value={it} data-testid={`reflection-item-${it.replace(/\s+/g, "-").toLowerCase()}`}>{it}</SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[#4A4540]">Share the memory in your words</Label>
                  <Textarea
                    data-testid="memory-description-input"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="My dad came to my school and brought me an ice cream…"
                    className={inputCls}
                    rows={6}
                  />
                </div>

                <div>
                  <Label className="text-[#4A4540]">How does this memory feel?</Label>
                  <Textarea
                    data-testid="memory-feeling-input"
                    value={form.feeling}
                    onChange={(e) => set("feeling", e.target.value)}
                    placeholder="Describe the feeling in your words"
                    className={inputCls}
                    rows={2}
                  />
                </div>

                <div>
                  <Label className="text-[#4A4540]">Photo reminder (optional)</Label>
                  <p className="text-xs text-[#8A7F76]">Which photo would you like to use with this memory later? Text only — photos are never uploaded. You'll choose the actual photo privately on your own device in the Ebook Studio.</p>
                  <Input
                    data-testid="memory-photo-reminder-input"
                    value={form.photo_reminder}
                    onChange={(e) => set("photo_reminder", e.target.value)}
                    placeholder="e.g. Beach photo, blue album"
                    className={inputCls}
                    maxLength={200}
                  />
                </div>

                <div>
                  <Button
                    data-testid="save-memory-btn"
                    onClick={submit}
                    disabled={saving || !canSave}
                    className="w-full rounded-full bg-[#E07A5A] py-6 text-white transition-transform hover:-translate-y-0.5 hover:bg-[#d66b4b] sm:w-auto sm:px-10"
                  >
                    <Check size={18} className="mr-1" /> {saving ? "Saving…" : "Save memory"}
                  </Button>
                  <p className="mt-2 text-xs text-[#8A7F76]">Saved securely to your account.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Side panel */}
          <aside className="min-w-0 space-y-6">
            {/* Life Season List */}
            <div className="rounded-2xl border border-[#4A4540]/10 bg-white p-6 shadow-sm" data-testid="life-season-list">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A84A32]">Life Season List</p>
              <h2 className="mt-1 font-serif text-2xl text-[#2C2A28]">Your journey</h2>
              <p className="mt-1 text-sm text-[#8A7F76]">
                {isPro ? "All seasons are unlocked with Lifetime Access." : "Childhood is included in your free trial."}
              </p>
              <div className="mt-4 space-y-2">
                {SEASONS.map((s) => {
                  const unlocked = isPro || s.id === FREE_SEASON;
                  return (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-[#4A4540]/10 px-3 py-2.5">
                      <span className="flex items-center gap-2 text-sm text-[#2C2A28]">
                        {unlocked
                          ? <LockOpen size={16} weight="duotone" className="text-[#7EA193]" />
                          : <LockSimple size={16} weight="fill" className="text-[#E0A94C]" />}
                        {s.label}
                      </span>
                      <span className={`text-xs font-medium ${unlocked ? "text-[#7EA193]" : "text-[#8A7F76]"}`}>
                        {unlocked ? "Unlocked" : "Locked"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Membership */}
            <div className="rounded-2xl bg-[#2E3B31] p-6 text-[#FBF4E8] shadow-sm" data-testid="membership-card">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E8C27A]">Membership</p>
              <h2 className="mt-1 font-serif text-2xl text-[#FBF4E8]">Lifetime Access</h2>
              {!isPro && (
                <p className="mt-1 text-sm text-[#C9CFC4]"><strong className="text-[#FBF4E8]">Free 10 day trial.</strong> Explore Childhood and create your first memories.</p>
              )}
              <p className="mt-3 flex items-end gap-2">
                <span className="font-serif text-4xl text-[#E8945A]">{PRICING.currency}{PRICING.lifetime.price}</span>
                <span className="pb-1 text-sm text-[#C9CFC4]">one payment</span>
              </p>
              <ul className="mt-4 space-y-2 text-sm text-[#C9CFC4]">
                {["All life seasons", "Unlimited memories and reflections", "Download and print your keepsakes", "Lifelong access to your saved journey"].map((b) => (
                  <li key={b} className="flex items-start gap-2"><Check size={15} weight="bold" className="mt-0.5 text-[#E8C27A]" /> {b}</li>
                ))}
              </ul>
              {!isPro ? (
                <Button
                  data-testid="side-upgrade-btn"
                  onClick={() => navigate("/upgrade")}
                  className="mt-5 w-full rounded-full bg-[#D1A33B] py-5 text-sm font-semibold text-[#2C2A28] hover:bg-[#c2962f]"
                >
                  Unlock my journey
                </Button>
              ) : (
                <p className="mt-5 rounded-full bg-white/10 py-2 text-center text-sm text-[#E8C27A]">You have Lifetime Access ✓</p>
              )}
              <div className="mt-4 border-t border-white/10 pt-4">
                <Button
                  data-testid="download-story-btn"
                  disabled
                  className="w-full cursor-not-allowed rounded-full bg-white/10 py-5 text-sm font-medium text-[#C9CFC4] opacity-80 hover:bg-white/10"
                >
                  <DownloadSimple size={16} className="mr-1" /> Download my story
                </Button>
                <p className="mt-1.5 text-center text-[11px] text-[#9DA79A]">Coming soon — downloadable keepsakes are being prepared.</p>
              </div>
            </div>

            {/* Gentle reminder */}
            <div className="rounded-2xl border border-[#E0A94C]/30 bg-[#FBF7EF] p-5" data-testid="gentle-reminder">
              <p className="flex items-center gap-2 font-serif text-lg text-[#2C2A28]">
                <Lightbulb size={18} weight="duotone" className="text-[#E0A94C]" /> A gentle reminder
              </p>
              <p className="mt-1 text-sm leading-relaxed text-[#6B635B]">
                Small details often hold the most meaning. A smell, a song, a familiar place or a few kind words can bring a whole memory back.
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
